import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';

// Types
interface FileWithPreview {
  file: File;
  id: string;
  preview?: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  url?: string;
}

interface DropZoneProps {
  onUpload: (files: File[]) => Promise<{ url: string }[]>;
  accept?: Record<string, string[]>;
  maxFiles?: number;
  maxSize?: number; // in MB
  multiple?: boolean;
  value?: string | string[];
  onChange?: (urls: string | string[]) => void;
  label?: string;
  helperText?: string;
  type?: 'image' | 'video' | 'document';
}

const defaultAccept = {
  'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif'],
  'video/*': ['.mp4', '.webm'],
  'application/pdf': ['.pdf'],
};

const DropZone: React.FC<DropZoneProps> = ({
  onUpload,
  accept = defaultAccept,
  maxFiles = 10,
  maxSize = 50,
  multiple = true,
  value,
  onChange,
  label = 'Перетащите файлы сюда или нажмите для выбора',
  helperText,
  type = 'image',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Generate unique ID
  const generateId = () => Math.random().toString(36).substring(2, 9);
  const [inputId] = useState(() => `dropzone-input-${generateId()}`);

  // Validate file
  const validateFile = useCallback((file: File): string | null => {
    // Check size
    if (file.size > maxSize * 1024 * 1024) {
      return `Файл слишком большой. Максимум: ${maxSize}MB`;
    }

    // Check type
    const acceptedTypes = Object.keys(accept).flatMap((key) => {
      if (key.endsWith('/*')) {
        return [key.replace('/*', '/')];
      }
      return [key];
    });

    const fileType = file.type.split('/')[0];
    void fileType; // Used for type checking
    const isValidType = acceptedTypes.some((t) => {
      if (t.endsWith('/')) {
        return file.type.startsWith(t);
      }
      return file.type === t;
    });

    if (!isValidType) {
      return `Неподдерживаемый тип файла: ${file.type}`;
    }

    return null;
  }, [accept, maxSize]);

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      handleFiles(selectedFiles);
    }
  }, []);

  // Process files
  const handleFiles = useCallback((newFiles: File[]) => {
    if (!multiple && newFiles.length > 1) {
      setError('Можно загрузить только один файл');
      return;
    }

    if (files.length + newFiles.length > maxFiles) {
      setError(`Максимум файлов: ${maxFiles}`);
      return;
    }

    const filesWithPreview: FileWithPreview[] = newFiles.map((file) => {
      const validationError = validateFile(file);
      return {
        file,
        id: generateId(),
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        status: validationError ? 'error' : 'pending',
        progress: 0,
        error: validationError || undefined,
      };
    });

    setFiles((prev) => [...prev, ...filesWithPreview]);

    // Auto-upload valid files
    const validFiles = filesWithPreview.filter((f) => f.status === 'pending');
    if (validFiles.length > 0) {
      uploadFiles(validFiles);
    }
  }, [files.length, maxFiles, multiple, validateFile]);

  // Upload files
  const uploadFiles = async (filesToUpload: FileWithPreview[]) => {
    // Set uploading status
    setFiles((prev) =>
      prev.map((f) =>
        filesToUpload.some((uf) => uf.id === f.id)
          ? { ...f, status: 'uploading' as const }
          : f
      )
    );

    try {
      const results = await onUpload(filesToUpload.map((f) => f.file));

      // Update with success
      setFiles((prev) =>
        prev.map((f) => {
          const index = filesToUpload.findIndex((uf) => uf.id === f.id);
          if (index !== -1 && results[index]) {
            return {
              ...f,
              status: 'success' as const,
              progress: 100,
              url: results[index].url,
            };
          }
          return f;
        })
      );

      // Notify parent
      const successfulUrls = filesToUpload
        .map((_, i) => results[i]?.url)
        .filter(Boolean);

      if (onChange && successfulUrls.length > 0) {
        if (multiple) {
          const allUrls = [
            ...(Array.isArray(value) ? value : []),
            ...successfulUrls,
          ];
          onChange(allUrls);
        } else {
          onChange(successfulUrls[0]);
        }
      }
    } catch (err) {
      // Update with error
      setFiles((prev) =>
        prev.map((f) =>
          filesToUpload.some((uf) => uf.id === f.id)
            ? { ...f, status: 'error' as const, error: 'Ошибка загрузки' }
            : f
        )
      );
    }
  };

  // Remove file
  const removeFile = (id: string) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  // Retry upload
  const retryUpload = (id: string) => {
    const file = files.find((f) => f.id === id);
    if (file) {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, status: 'pending' as const, error: undefined } : f
        )
      );
      uploadFiles([{ ...file, status: 'pending' }]);
    }
  };

  // Get icon for file type
  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <ImageIcon />;
    if (file.type.startsWith('video/')) return <VideoIcon />;
    if (file.type === 'application/pdf') return <PdfIcon />;
    return <UploadIcon />;
  };

  // Get status icon
  const getStatusIcon = (status: FileWithPreview['status']) => {
    switch (status) {
      case 'uploading':
        return <CircularProgress size={20} />;
      case 'success':
        return <SuccessIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  return (
    <Box>
      {/* Drop zone */}
      <Paper
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        sx={{
          p: 3,
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'divider',
          bgcolor: isDragging ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s',
          textAlign: 'center',
          '&:hover': {
            borderColor: 'primary.light',
            bgcolor: 'action.hover',
          },
        }}
      >
        <input
          type="file"
          accept={Object.values(accept).flat().join(',')}
          multiple={multiple}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          id={inputId}
        />
        <label htmlFor={inputId} style={{ cursor: 'pointer' }}>
          <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {helperText || `Максимум ${maxFiles} файлов, до ${maxSize}MB каждый`}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            {type === 'image' && (
              <>
                <Chip label="JPG" size="small" />
                <Chip label="PNG" size="small" />
                <Chip label="WebP" size="small" />
                <Chip label="GIF" size="small" />
              </>
            )}
            {type === 'video' && (
              <>
                <Chip label="MP4" size="small" />
                <Chip label="WebM" size="small" />
              </>
            )}
            {type === 'document' && (
              <>
                <Chip label="PDF" size="small" />
              </>
            )}
          </Box>
        </label>
      </Paper>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* File list */}
      {files.length > 0 && (
        <List sx={{ mt: 2 }}>
          {files.map((fileItem) => (
            <ListItem
              key={fileItem.id}
              secondaryAction={
                <IconButton edge="end" onClick={() => removeFile(fileItem.id)}>
                  <CloseIcon />
                </IconButton>
              }
              sx={{
                bgcolor: 'background.paper',
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {/* Preview or icon */}
              {fileItem.preview ? (
                <Box
                  component="img"
                  src={fileItem.preview}
                  alt={fileItem.file.name}
                  sx={{
                    width: 56,
                    height: 56,
                    objectFit: 'cover',
                    borderRadius: 1,
                    mr: 2,
                  }}
                />
              ) : (
                <ListItemIcon>{getFileIcon(fileItem.file)}</ListItemIcon>
              )}

              <ListItemText
                primary={fileItem.file.name}
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon(fileItem.status)}
                    <Typography variant="caption" color="text.secondary">
                      {(fileItem.file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                    {fileItem.error && (
                      <Typography variant="caption" color="error">
                        {fileItem.error}
                      </Typography>
                    )}
                  </Box>
                }
              />

              {/* Retry button for errors */}
              {fileItem.status === 'error' && (
                <Chip
                  label="Повторить"
                  size="small"
                  onClick={() => retryUpload(fileItem.id)}
                  sx={{ mr: 1 }}
                />
              )}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default DropZone;